import React, { useState, useEffect, useMemo } from "react";
import Datatable from "../../../Components/Datatable";
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../../context/AuthContext';
import axios from "axios";
import { API_BASE_URL } from "../../../../../../Helpers/apiConfig";

export const AdminCourseList = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    // -- State'ler --
    const [sections, setSections] = useState([]);
    const [activeCourses, setActiveCourses] = useState([]);
    const [passiveCourses, setPassiveCourses] = useState([]);
    const [selectedSectionId, setSelectedSectionId] = useState("");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // -- Yardımcı: HTML Tag'leri Render Etme --
    const renderHtml = (rawHtml) => {
        return <span dangerouslySetInnerHTML={{ __html: rawHtml }} />;
    };

    // -- API: Section Listesini Getir --
    const fetchSections = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/SectionManager/GetList`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            if (res.data.success) {
                setSections(res.data.data);
            }
        } catch (err) {
            setMsg("Section listesi yüklenemedi.");
        }
    };

    // -- API: Section ID'ye göre Kursları Getir --
    const fetchCoursesBySection = async (sectionId) => {
        if (!sectionId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/CourseManager/GetCoursesBySection`, {
                params: { id: sectionId },
                headers: { Authorization: `Bearer ${user.token}` }
            });

            if (res.data.success) {
                const allData = res.data.data;
                // sortId'ye göre küçükten büyüğe sırala
                const sortedData = allData.sort((a, b) => a.sortId - b.sortId);

                setActiveCourses(sortedData.filter(c => c.isActive && !c.isDeleted));
                setPassiveCourses(sortedData.filter(c => !c.isActive && !c.isDeleted));
            }
        } catch (err) {
            setMsg("Kurslar yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // -- Tablo Sütunları --
    const columns = useMemo(() => [
        {
            Header: "Sıra (SortId)",
            accessor: "sortId",
            width: 70
        },
        {
            Header: t('courseName'),
            accessor: 'courseName',
            Cell: ({ value }) => renderHtml(value) // HTML tag desteği
        },
        {
            Header: t('courseDescription'),
            accessor: 'courseDesc',
        },
        {
            Header: t('isActive'),
            accessor: (row) => (row.isActive ? "✅ Aktif" : "❌ Pasif"),
        },
        {
            Header: t('editCourse'),
            accessor: (row) => (
                <div className="btn-group shadow-sm">
                    <button className="btn btn-warning btn-sm" onClick={() => window.location.href = `/admin/editcourse/${row.id}`}>
                        <i className="fa fa-edit"></i> {t('edit')}
                    </button>
                    {/* Diğer aksiyon butonlarını buraya ekleyebilirsin */}
                </div>
            ),
        }
    ], [t]);

    useEffect(() => {
        fetchSections();
    }, []);

    const handleSectionChange = (e) => {
        const id = e.target.value;
        setSelectedSectionId(id);
        fetchCoursesBySection(id);
    };

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <div className="row justify-content-center">
                <div className="col-12 col-xl-11">

                    {/* Başlık Kartı */}
                    <div className="card shadow-sm border-0 mb-4 bg-white">
                        <div className="card-body p-4 text-center">
                            <h2 className="fw-bold text-primary mb-2">{t("courseList")}</h2>
                            {msg && <div className="alert alert-danger">{msg}</div>}

                            <div className="row justify-content-center mt-4">
                                <div className="col-md-6 col-lg-4">
                                    <label className="form-label fw-bold text-secondary">Kategori (Section) Seçiniz</label>
                                    <select
                                        className="form-select form-select-lg shadow-sm"
                                        value={selectedSectionId}
                                        onChange={handleSectionChange}
                                        style={{ borderLeft: "5px solid #0d6efd" }}
                                    >
                                        <option value="">--- Section Seçin ---</option>
                                        {sections.map(sec => (
                                            <option key={sec.id} value={sec.id}>{sec.sectionName}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center my-5 py-5">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2 fw-bold">Kurslar Yükleniyor...</p>
                        </div>
                    ) : (
                        selectedSectionId && (
                            <div className="fade-in">
                                {/* Aktif Kurslar */}
                                <div className="card shadow-sm border-0 mb-5 overflow-hidden">
                                    <div className="card-header bg-success text-white py-3">
                                        <h5 className="mb-0 fw-bold"><i className="fa fa-check-circle me-2"></i>{t("courseList")} (Aktif)</h5>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <Datatable data={activeCourses} columns={columns} tableBaslik={t("courseList")} />
                                        </div>
                                    </div>
                                </div>

                                {/* Pasif Kurslar */}
                                <div className="card shadow-sm border-0 mb-5 overflow-hidden">
                                    <div className="card-header bg-secondary text-white py-3">
                                        <h5 className="mb-0 fw-bold"><i className="fa fa-times-circle me-2"></i>{t("passivecourseList")}</h5>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <Datatable data={passiveCourses} columns={columns} tableBaslik={t("passivecourseList")} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {!selectedSectionId && !loading && (
                        <div className="alert alert-info text-center shadow-sm py-4">
                            <i className="fa fa-arrow-up d-block mb-2 fs-3"></i>
                            Kursları listelemek için lütfen yukarıdan bir <strong>Section</strong> seçiniz.
                        </div>
                    )}

                </div>
            </div>

            {/* Mobil Uyumlu Stil */}
            <style jsx>{`
                .fade-in {
                    animation: fadeIn 0.5s ease-in;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (max-width: 768px) {
                    .card-header h5 { font-size: 1rem; }
                    .btn-sm { padding: 0.25rem 0.5rem; font-size: 0.75rem; }
                }
            `}</style>
        </div>
    );
};

export default AdminCourseList;